import React, { useState, useEffect } from 'react';
import { friendlyDeleteError } from '../utils/deleteError';
import { useMoney } from '../hooks/useMoney';
import idColumn from './ui/idColumn';
import { useNavigate } from 'react-router-dom';
import '../styles/components/MenuView.css';
import DataTable from './DataTable';
import ContentCard from './ContentCard';
import Icon from './Icon';
import { MetricCard } from './MetricCards';
import MarkdownText from './ui/MarkdownText';
import ConfirmDialog from './ui/ConfirmDialog';
import { loadMenu, refreshMenuFromApi, archiveMenuItem, restoreMenuItem, MenuCategory, MenuItem, MENU_TABS, MENU_TAB_CATERERS } from '../services/MenuService';
import { MENU_ITEM_EDIT_KEY, MenuItemEditContext } from './FormPage';
import { useNotifications } from './NotificationProvider';
import { fmtId } from '../utils/jiraId';
import { loadVendorData } from '../data/VendorLoader';
import EmptyState from './EmptyState';

const menuTabs = MENU_TABS;

interface MenuViewProps {
  menuTab?: string;
  onMenuTabChange?: (tab: string) => void;
  menuView?: 'card' | 'list';
  onMenuViewChange?: (view: 'card' | 'list') => void;
}

const MenuView: React.FC<MenuViewProps> = ({ menuTab: externalTab, onMenuTabChange, menuView: externalView, onMenuViewChange }) => {
  const money = useMoney();
  const navigate = useNavigate();
  const [internalTab, setInternalTab] = useState('Traditional');
  const activeTab = externalTab ?? internalTab;
  const setActiveTab = (tab: string) => { setInternalTab(tab); onMenuTabChange?.(tab); };
  const [internalView, setInternalView] = useState<'card' | 'list'>('card');
  const viewMode = externalView ?? internalView;
  const setViewMode = (v: 'card' | 'list') => { setInternalView(v); onMenuViewChange?.(v); };

  const { showError } = useNotifications();
  const [allCategories, setAllCategories] = useState<MenuCategory[]>(() => loadMenu());
  // 3-state (active/archived/all), matching Venues — lives in Header's
  // breadcrumb now instead of a button floating in the page body below it.
  const [archiveFilter, setArchiveFilter] = useState<'active' | 'archived' | 'all'>('active');
  const [archiveTarget, setArchiveTarget] = useState<MenuItem | null>(null);

  // Pull from API on mount
  useEffect(() => {
    refreshMenuFromApi().then(cats => { if (cats.length > 0) setAllCategories(cats); });
  }, []);

  // Listen for header "Add Item" event
  useEffect(() => {
    const handler = () => navigate('/menu-item/new/');
    window.addEventListener('menu-add-item', handler);
    return () => window.removeEventListener('menu-add-item', handler);
  }, [navigate]);

  const openEditItem = (tab: string, sectionTitle: string, itemIndex: number, item: MenuItem) => {
    const ctx: MenuItemEditContext = {
      tab,
      section: sectionTitle,
      index: itemIndex,
      name: item.name,
      desc: item.desc,
      tags: item.tags,
      allowance: item.allowance,
      photo_url: item.photo_url,
      servings: item.servings,
      ingredients: item.ingredients,
      dietary_flags: item.dietary_flags,
    };
    sessionStorage.setItem(MENU_ITEM_EDIT_KEY, JSON.stringify(ctx));
    navigate(item.id ? `/menu-item/${fmtId('MI', item.id)}` : '/menu-item/new');
  };

  const handleArchiveItem = (item: MenuItem) => {
    setArchiveTarget(item);
  };

  const handleRestoreItem = async (item: MenuItem) => {
    if (!item.id) return;
    try {
      setAllCategories(await restoreMenuItem(item.id));
    } catch (err) {
      showError(friendlyDeleteError(err, { record: 'menu item', action: 'restore' }));
    }
  };

  const categories = allCategories
    .filter(c => c.tab === activeTab)
    .map(c => ({
      ...c,
      items: c.items.filter(item => {
        if (archiveFilter === 'active') return !item.archived;
        if (archiveFilter === 'archived') return !!item.archived;
        return true;
      }),
    }));

  // Totals shown as metric cards: every tab, plus the tab on screen.
  const itemCounts = (() => {
    const matchesArchiveFilter = (item: MenuItem) => {
      if (archiveFilter === 'active') return !item.archived;
      if (archiveFilter === 'archived') return !!item.archived;
      return true;
    };
    const total = allCategories.reduce((sum, c) => sum + c.items.filter(matchesArchiveFilter).length, 0);
    const activeTabCount = categories.reduce((sum, c) => sum + c.items.length, 0);
    return { total, activeTab: activeTabCount };
  })();

  const cateringVendors = MENU_TAB_CATERERS;

  // Build a name→vendorId map for caterers that exist in the vendor registry
  const [vendorIdByName, setVendorIdByName] = useState<Record<string, string>>({});
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('menu-archived-sync', { detail: archiveFilter }));
  }, [archiveFilter]);

  useEffect(() => {
    const onSet = (e: Event) => setArchiveFilter((e as CustomEvent).detail);
    window.addEventListener('menu-archived-set', onSet);
    return () => window.removeEventListener('menu-archived-set', onSet);
  }, []);

  useEffect(() => {
    loadVendorData().then((all) => {
      const map: Record<string, string> = {};
      all.forEach(v => {
        const key = v.name.toLowerCase().trim();
        map[key] = v.id;
      });
      setVendorIdByName(map);
    }).catch(() => { /* leave map empty if load fails */ });
  }, []);

  return (
    <div className="wt-container fade-in">
        <section className="stats-grid">
          <MetricCard label="Menu Items" value={itemCounts.total} tone="blue" icon="list" />
          <MetricCard label={`${activeTab} Items`} value={itemCounts.activeTab} tone="green" icon="utensils" />
        </section>
        <div className="menu-grid">
          <div className="menu-content-main">
            {viewMode === 'card' ? (
              categories.length === 0 || categories.every(c => c.items.length === 0) ? (
                <EmptyState
                  icon="utensils"
                  title={(archiveFilter === 'archived') ? 'No archived items' : 'No menu items yet'}
                  description={(archiveFilter === 'archived') ? undefined : 'Click Add Item to get started.'}
                />
              ) : (
              <div className="menu-items">
                {categories.map((cat, idx) => (
                  <div key={idx} className="menu-category-section">
                    {cat.items.length > 0 && (
                      <h3 className="menu-category-title">{cat.title}</h3>
                    )}
                    <div className="menu-items-grid">
                      {cat.items.map((item, iIdx) => (
                        <div key={iIdx} className="menu-item-clickable" onClick={() => openEditItem(activeTab, cat.title, iIdx, item)}>
                          <ContentCard
                            title={item.name}
                            subtitle={cat.title}
                            description={item.desc}
                            pricing={item.allowance ? { amount: item.allowance, currency: money.symbol, unit: 'guest' } : undefined}
                            tags={item.tags}
                          />
                          {(archiveFilter === 'archived') ? (
                            <button
                              className="menu-item-restore-btn"
                              onClick={(e) => { e.stopPropagation(); handleRestoreItem(item); }}
                              title="Restore item"
                            >
                              Restore
                            </button>
                          ) : (
                            <button
                              className="menu-item-delete-btn"
                              onClick={(e) => { e.stopPropagation(); handleArchiveItem(item); }}
                              title="Archive item"
                            >
                              <Icon name="close" size={14} />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              )
            ) : (
              <DataTable
                recordList={{ kind: 'menuItem' }}
                data={categories.flatMap(cat => cat.items.map((item, iIdx) => ({ ...item, category: cat.title, _catTitle: cat.title, _iIdx: iIdx })))}
                columns={[
                  idColumn('menuItem'),
                  { key: 'name', header: 'Item', render: (_: string, row: any) => (
                    <div><strong>{row.name}</strong><MarkdownText className="menu-item-desc">{row.desc}</MarkdownText></div>
                  )},
                  { key: 'category', header: 'Category', width: '150px' },
                  { key: 'allowance', header: 'Allowance', width: '130px', render: (v: number) => v ? money.format(v) : '—' },
                  { key: 'tags', header: 'Tags', render: (_: string[], row: any) => (
                    <div className="tag-pills">{row.tags.map((t: string) => <span key={t} className="tag-pill">{t}</span>)}</div>
                  )},
                  { key: 'actions', header: '', width: '90px', render: (_: unknown, row: any) => (
                    (archiveFilter === 'archived') ? (
                      <button className="menu-item-restore-btn" onClick={(e) => { e.stopPropagation(); handleRestoreItem(row); }}>Restore</button>
                    ) : (
                      <button className="menu-item-delete-btn" onClick={(e) => { e.stopPropagation(); handleArchiveItem(row); }} title="Archive item">
                        <Icon name="close" size={14} />
                      </button>
                    )
                  )},
                ]}
                onRowClick={(row: any) => openEditItem(activeTab, row._catTitle, row._iIdx, row)}
                compact
                hoverable
                emptyMessage={(archiveFilter === 'archived') ? 'No archived items.' : 'No menu items.'}
              />
            )}
          </div>

          <aside className="menu-sidebar">
            <div className="menu-card-mini vendor-card">
              <h3>Catering Vendors</h3>
              <ul className="vendor-list">
                {(cateringVendors[activeTab] || []).map(v => {
                  const vendorId = vendorIdByName[v.toLowerCase().trim()];
                  return (
                    <li key={v}>
                      <button
                        type="button"
                        className="menu-vendor-link"
                        onClick={() => vendorId
                          ? navigate(`/vendor/${fmtId('VD', vendorId)}`)
                          : navigate(`/vendors?search=${encodeURIComponent(v)}`)
                        }
                      >
                        {v}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>

            <div className="menu-card-mini dietary-card">
              <h3>Menu Notes</h3>
              <ul className="dietary-list">
                {activeTab === 'Traditional' && (
                  <>
                    <li><span>4 ethnic groups represented</span></li>
                    <li><span>Corner stands run throughout</span></li>
                    <li><span>Waiter service for drinks</span></li>
                  </>
                )}
                {activeTab === 'Reception (VIP)' && (
                  <>
                    <li><span>VVIP table: all courses</span></li>
                    <li><span>VIP tables: main + dessert</span></li>
                    <li><span>Allowances listed per dish</span></li>
                  </>
                )}
                {activeTab === 'After Party' && (
                  <>
                    <li><span>180 expected attendees</span></li>
                    <li><span>Tapas style — no sit-down</span></li>
                    <li><span>Hans & Renee for desserts</span></li>
                    <li><span>Full drinks menu in this tab</span></li>
                    <li><span>Non-alcoholic options available</span></li>
                  </>
                )}
              </ul>
            </div>
          </aside>
        </div>
      <ConfirmDialog
        open={archiveTarget !== null}
        onClose={() => setArchiveTarget(null)}
        onConfirm={async () => {
          const id = archiveTarget?.id;
          setArchiveTarget(null);
          if (!id) return;
          try {
            setAllCategories(await archiveMenuItem(id));
          } catch (err) {
            showError(friendlyDeleteError(err, { record: 'menu item', action: 'archive' }));
          }
        }}
        title={`Archive "${archiveTarget?.name}"?`}
        description="It will be hidden from the active list but not permanently deleted. You can restore it later from the Archived filter."
        confirmLabel="Archive"
      />
    </div>
  );
};

export default MenuView;
